import { ImageRun, Paragraph, TextRun } from 'docx';

// Constants
import {
  FONT_FAMILY,
  FONT_SIZES,
  ICON_SIZE,
  THIN_BORDER_BOTTOM,
  COLORS,
  SPACINGS,
} from '../constants';

/** Run config for richTextParser matching Default Poppins 10pt body style */
export const DEFAULT_RUN_CONFIG = {
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZES.base,
  color: COLORS.foreground,
} as const;

/** Object form of TextRun constructor options */
type TextRunOptions = Extract<ConstructorParameters<typeof TextRun>[0], object>;

/** Creates a body TextRun with optional overrides */
export const bodyRun = (text: string, options?: Partial<TextRunOptions>) =>
  new TextRun({
    text,
    font: FONT_FAMILY,
    size: FONT_SIZES.base,
    color: COLORS.foreground,
    ...options,
  });

/** Creates a PNG icon ImageRun or returns null if data is missing */
export const iconImageRun = (
  data: Uint8Array | null,
  size: number,
  label = 'Icon',
) => {
  if (!data) return null;
  return new ImageRun({
    data,
    transformation: { width: size, height: size },
    type: 'png',
    altText: { title: label, description: label, name: label.toLowerCase() },
  });
};

/** Returns an icon ImageRun plus a spacer for inline icon-text layout */
export const buildIconPrefix = (
  iconData: Uint8Array | null,
  label = 'Icon',
): (ImageRun | TextRun)[] => {
  const icon = iconImageRun(iconData, ICON_SIZE, label);
  if (!icon) return [];
  return [icon, new TextRun({ text: '  ', font: FONT_FAMILY })];
};

/** Creates a thin horizontal divider paragraph */
export const dividerParagraph = (
  spacing: { before?: number; after?: number } = {
    before: SPACINGS.large,
    after: SPACINGS.large,
  },
) =>
  new Paragraph({
    border: THIN_BORDER_BOTTOM,
    spacing,
  });

/** Creates a section title paragraph */
const sectionTitle = (
  title: string,
  spacing: { before?: number; after?: number },
) =>
  new Paragraph({
    spacing,
    children: [
      new TextRun({
        text: title,
        font: FONT_FAMILY,
        size: FONT_SIZES['5xl'],
        bold: true,
        allCaps: true,
        color: COLORS.foreground,
      }),
    ],
  });

/** Creates a sidebar section title paragraph */
export const sidebarSectionTitle = (
  title: string,
  before = SPACINGS.sectionHeading,
) => sectionTitle(title, { before, after: 0 });

/** Creates a content area section title */
export const contentSectionTitle = (title: string) =>
  sectionTitle(title, { before: SPACINGS.small, after: SPACINGS.small });
